import React, { useEffect } from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "types/store";
import numeral from "numeral";
import moment from "moment";

import PropTypes from "prop-types";

import Select from "react-select";

import { Button, ButtonGroup, getRange, Icon } from "aible";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import SearchInputDelayed from "components/Inputs/SearchInput/SearchInputDelayed";
import Tooltip from "components/Inputs/Tooltip";

import * as nextReducer from "containers/Next/NextReducer";
import * as homeReducer from "containers/Home/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";

import { isEnabled } from "utils/featureFlags";

import { AutoSizer, Grid } from "react-virtualized";
import { LineChart, Line, XAxis, YAxis, ReferenceArea, Tooltip as ChartTooltip, ReferenceLine } from "recharts";

import ChartLineIcon from "images/chart-line-regular.svg";
import ChartWaterfallIcon from "images/chart-waterfall-regular.svg";
import MessageLineIcon from "images/message-lines-regular.svg";

import styles from "./style.module.css";
import WaterfallChart from "components/ChatGPT/Dashboard/WaterfallChart";
import MarkdownPanel from "components/ChatGPT/MarkdownPanel";
import KPISettings, { KPISettingsActions } from "components/ChatGPT/Dashboard/KPISettings";

import {
  KPI_TOP_CHARTS_COUNT,
  formatNumber,
  CHART_KEY_LOOKUPS,
  getDisplayValueFromChart,
  getDisplayReferenceValueFromChart,
  getDisplayCurrentValueFromChart
} from "containers/ChatGPT/reducer";
import KPIDeepDive from "components/KPIAnalysis/KPIDeepDive";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";

const SUMMARIZE_HOVER_INTERVAL = 1000;

const getSearchTitle = (chart, includeChildren = false) => {
  let currTitle = chart.title + "\n" + chart.fullLabel;
  if (chart.childCharts?.length && includeChildren) {
    const subTitles = chart.childCharts.map((c) => getSearchTitle(c));
    currTitle += subTitles.join("\n");
  }

  return String(currTitle).toLowerCase();
};

const filterCharts = (chart, searchVal, KPISortKey) => {
  if (!chart) {
    return false;
  }
  if (searchVal) {
    const includeChildren = KPISortKey === "REPORT";
    const cleanTitle = getSearchTitle(chart, includeChildren);
    return cleanTitle.includes(searchVal.toLowerCase());
  }
  // Ignore Charts without enough data
  return chart.data?.length > 1;
};

const KPIDashboard = (props = {}) => {
  const { id, datasetId } = props;

  const dispatch = useDispatch();

  const cNextId = useSelector((state) => nextReducer.getCurrentNextId(state));
  const nextId = id ?? cNextId;
  const nDatasetId = useSelector((state) => nextReducer.getDatasetId(state, nextId));
  const retDatasetId = datasetId ?? nDatasetId;
  const dataset = useSelector((state) => homeReducer.getDataset(state, retDatasetId));

  const [expandChart, updateExpandChart] = useState(undefined); // Modal chart
  const [searchVal, updateSearchVal] = useState("");

  const KPISortKeyOptions = useSelector((state) => chatGPTReducer.getKPISortKeyOptions(state));

  const [activeTab, updateActiveTab] = useState(
    KPISortKeyOptions.find((o) => o.value === "CHANGE") ? "CHANGE" : KPISortKeyOptions[0].value
  );
  const [topLevelView, updateTopLevelView] = useState(true); // For verizon
  const [groupBusinessSubgroups, updateGroupBusinessSubgroups] = useState(isEnabled("kpi-analysis-onex")); // for verizon
  const [useOneXMetrics, updateUseOneXMetrics] = useState(false); // for verizon
  const [showSettings, updateShowSettings] = useState(false);

  const KPIChartsOrder = useSelector((state) => chatGPTReducer.getKPIChartsOrder(state));
  const isLoadingKPIAnalysis = useSelector((state) => chatGPTReducer.getIsLoadingKPIAnalysis(state));

  const KPIChartsLoaded = useSelector((state) => chatGPTReducer.getKPIChartsLoaded(state));
  const KPICombinations = useSelector((state) => chatGPTReducer.getKPICombinations(state));

  const KPISortKey = useSelector((state) => chatGPTReducer.getKPISortKey(state));

  const refreshKPIAnalysis = () => dispatch(chatGPTReducer.refreshKPIAnalysis());

  const isLoading =
    !(dataset?.id && (dataset?.status_name === "DONE" || dataset?.status_name === "ERROR")) ||
    isLoadingKPIAnalysis ||
    !KPIChartsOrder;

  if (isLoading) {
    return (
      <div className={styles.kpiDashboard}>
        <div className={styles.dashboardWrapper}>
          <div className={styles.loadHolder} style={{ marginTop: "20px" }}>
            <LoadingIndicator style={{ margin: "0px" }} />
          </div>
          {isLoadingKPIAnalysis ? (
            KPICombinations > 0 ? (
              <div style={{ marginTop: "10px" }}>
                {`Analyzing ${numeral(KPICombinations).format("0,0")} driver combinations...`}
              </div>
            ) : (
              <div style={{ marginTop: "10px" }}>{"Analyzing What's Changed..."}</div>
            )
          ) : null}
          {KPIChartsLoaded ? <div style={{ marginTop: "10px" }}>{`${KPIChartsLoaded} charts loaded...`}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <KPISettingsModal showModal={showSettings} updateShowModal={updateShowSettings} />
      <div className={styles.kpiDashboard}>
        <DashboardTitle
          searchVal={searchVal}
          updateSearchVal={updateSearchVal}
          handleReanalyze={() => {
            refreshKPIAnalysis();
          }}
          updateShowSettings={updateShowSettings}
          showSettings={showSettings}
          isLoading={isLoading}
          topLevelView={topLevelView}
          updateTopLevelView={updateTopLevelView}
          activeTab={activeTab}
          updateActiveTab={updateActiveTab}
        />
        <div className={styles.dashboardWrapper}>
          {activeTab === "SUMMARY" ? (
            <ExecSummaryBody
              {...props}
              KPIChartsOrder={KPIChartsOrder}
              expandChart={expandChart}
              updateExpandChart={updateExpandChart}
              searchVal={searchVal}
              updateSearchVal={updateSearchVal}
            />
          ) : activeTab === "ONEX" ? (
            <React.Fragment>
              <DashboardHeader
                {...props}
                expandChart={expandChart}
                updateExpandChart={updateExpandChart}
                searchVal={searchVal}
                updateSearchVal={updateSearchVal}
                topLevelView={topLevelView}
                updateTopLevelView={updateTopLevelView}
                groupBusinessSubgroups={groupBusinessSubgroups}
                updateGroupBusinessSubgroups={updateGroupBusinessSubgroups}
                useOneXMetrics={useOneXMetrics}
                updateUseOneXMetrics={updateUseOneXMetrics}
                activeTab={activeTab}
                updateActiveTab={updateActiveTab}
              />
              <OneXBody
                {...props}
                expandChart={expandChart}
                updateExpandChart={updateExpandChart}
                searchVal={searchVal}
                updateSearchVal={updateSearchVal}
                topLevelView={topLevelView}
                updateTopLevelView={updateTopLevelView}
                groupBusinessSubgroups={groupBusinessSubgroups}
                updateGroupBusinessSubgroups={updateGroupBusinessSubgroups}
                useOneXMetrics={useOneXMetrics}
                updateUseOneXMetrics={updateUseOneXMetrics}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <DashboardHeader
                {...props}
                expandChart={expandChart}
                updateExpandChart={updateExpandChart}
                searchVal={searchVal}
                updateSearchVal={updateSearchVal}
                topLevelView={topLevelView}
                updateTopLevelView={updateTopLevelView}
                groupBusinessSubgroups={groupBusinessSubgroups}
                updateGroupBusinessSubgroups={updateGroupBusinessSubgroups}
                useOneXMetrics={useOneXMetrics}
                updateUseOneXMetrics={updateUseOneXMetrics}
                activeTab={activeTab}
                updateActiveTab={updateActiveTab}
              />
              <DashboardBody
                {...props}
                KPIChartsOrder={KPIChartsOrder}
                expandChart={expandChart}
                updateExpandChart={updateExpandChart}
                searchVal={searchVal}
                updateSearchVal={updateSearchVal}
                topLevelView={topLevelView}
                updateTopLevelView={updateTopLevelView}
                groupBusinessSubgroups={groupBusinessSubgroups}
                updateGroupBusinessSubgroups={updateGroupBusinessSubgroups}
                useOneXMetrics={useOneXMetrics}
                updateUseOneXMetrics={updateUseOneXMetrics}
                activeTab={activeTab}
                updateActiveTab={updateActiveTab}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

const DashboardTitle = (props = {}) => {
  const { searchVal, updateSearchVal, activeTab, updateActiveTab, showSettings, updateShowSettings } = props;

  const dispatch = useDispatch();

  const KPISortOrder = useSelector((state) => chatGPTReducer.getKPISortOrder(state));

  const KPISortKeyOptions = useSelector((state) => chatGPTReducer.getKPISortKeyOptions(state));
  const KPISortOrderOptions = useSelector((state) => chatGPTReducer.getKPISortOrderOptions(state));

  const updateKPISortKey = (val) => dispatch(chatGPTReducer.updateKPISortKey(val));
  const updateKPISortOrder = (val) => dispatch(chatGPTReducer.updateKPISortOrder(val));

  const sortKPICharts = () => dispatch(chatGPTReducer.sortKPICharts());

  const showSortDirection = activeTab !== "REPORT" && activeTab !== "SUMMARY";

  return (
    <div className={styles.dashboardTitleWrapper}>
      <div className={styles.dashboardTitleSearch}>
        <SearchInputDelayed
          value={searchVal}
          onChange={(value) => updateSearchVal(value)}
          inputClass={styles.dashboardTitleSearchInput}
          delay={750}
          responsive
        />
      </div>
      <div className={styles.dashboardTitleLabel}>{"Sort by"}</div>
      <ButtonGroup>
        {KPISortKeyOptions.map((o) => (
          <Button
            key={"Sort_by_" + o.label}
            className={styles.dashboardTitleButton}
            onClick={() => {
              updateActiveTab(o.value);
              if (o.value === "ONEX") {
                // OneX view uses REPORT values. It's just a different display layer
                updateKPISortKey("REPORT");
              } else {
                updateKPISortKey(o.value);
                sortKPICharts();
              }
            }}
            active={activeTab === o.value}
            type={"secondary"}
          >
            {o.label}
          </Button>
        ))}
      </ButtonGroup>
      {showSortDirection && (
        <React.Fragment>
          <div className={styles.dashboardTitleSubtitle}>{"and"}</div>
          <div className={styles.dashboardTitleSelect}>
            <Select
              options={KPISortOrderOptions}
              value={KPISortOrderOptions.find((o) => o.value === KPISortOrder)}
              onChange={(o) => {
                updateKPISortOrder(o.value);
                sortKPICharts();
              }}
              menuPlacement={"top"}
            />
          </div>
        </React.Fragment>
      )}
      <Button
        onClick={(e) => {
          updateShowSettings(!showSettings);
        }}
        style={{
          fontWeight: "normal",
          marginLeft: "auto",
          fontSize: "16px",
          padding: "9px"
        }}
        active={showSettings}
      >
        <Icon type={"gear"} />
      </Button>
    </div>
  );
};

const KPISettingsModal = ({ showModal, updateShowModal }) => {
  return (
    <Dialog
      open={showModal}
      onOpenChange={() => {
        updateShowModal(false);
      }}
    >
      <DialogContent size="full">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <div className={styles.KPISettingsModalBody}>
          <KPISettings hideActions />
        </div>
        <DialogFooter>
          <KPISettingsActions onClick={() => updateShowModal(false)} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

KPISettingsModal.propTypes = {
  showModal: PropTypes.bool,
  updateShowModal: PropTypes.func
};

const DashboardHeader = (props = {}) => {
  const {
    expandChart,
    updateExpandChart,
    searchVal,
    topLevelView,
    updateTopLevelView,
    groupBusinessSubgroups,
    updateGroupBusinessSubgroups,
    useOneXMetrics,
    updateUseOneXMetrics,
    activeTab
  } = props;

  let sortKeyOverride = undefined;
  if (!topLevelView && activeTab === "REPORT") {
    sortKeyOverride = "CHANGE";
  }

  const KPISortKey = useSelector((state) => chatGPTReducer.getKPISortKey(state));

  const KPITopCharts = useSelector((state) => chatGPTReducer.getKPITopCharts(state, sortKeyOverride)) ?? [];

  const hasPinnedCharts = useSelector((state) => chatGPTReducer.getKPIHasPinnedCharts(state));

  const chartSummaries = useSelector((state) => chatGPTReducer.getKPIChartSummaries(state));
  const analysisResults = useSelector((state) => chatGPTReducer.getKPIAnalysisResults(state));

  const KPICharts = useSelector((state) => chatGPTReducer.getKPICharts(state));
  const KPICombinations = useSelector((state) => chatGPTReducer.getKPICombinations(state));

  let showTopCharts = KPITopCharts.filter((d) => {
    // if (!KPISearchMain && KPISortKey === "REPORT" && searchVal?.length > 0) {
    //   return filterCharts(d, searchVal, "ACTUAL");
    // }
    return filterCharts(d, searchVal, sortKeyOverride ?? KPISortKey);
  });

  let searchCharts = [];
  if (searchVal && showTopCharts.length < KPI_TOP_CHARTS_COUNT) {
    const KPIChartsWithSubgroups = [...KPICharts.map((item) => [item, ...item.subgroups])].flat();
    searchCharts = KPIChartsWithSubgroups.filter((d) => {
      if (showTopCharts.find((c) => d.title === c.title)) {
        return false;
      }
      return filterCharts(d, searchVal, sortKeyOverride ?? KPISortKey);
    }).slice(0, KPI_TOP_CHARTS_COUNT - showTopCharts.length);
  }
  let retCharts = [...showTopCharts, ...searchCharts];

  let dashTitle = `Top ${retCharts.length} Changes`;
  if (hasPinnedCharts) {
    dashTitle = `Pinned KPIs`;
  }
  if (searchVal) {
    dashTitle = `Top "${searchVal}" Changes`;
  } else if (KPICombinations > 0) {
    dashTitle += ` (${numeral(KPICombinations).format("0,0")} driver combinations evaluated)`;
  }

  return (
    <div className={styles.dashboardHeader}>
      <div className={styles.dashboardHeaderTitle}>
        <div className={styles.dashboardTitle} style={{ marginRight: "10px" }}>
          {dashTitle}
        </div>
        {(activeTab === "REPORT" || activeTab === "ONEX") && (
          <div
            className={styles.searchMainCheckboxWrapper}
            onClick={() => {
              updateTopLevelView(!topLevelView);
            }}
          >
            <input
              className={styles.searchMainCheckbox}
              type="checkbox"
              checked={topLevelView}
              onChange={() => {}}
              readOnly
            />
            <div className={styles.searchMainCheckboxText}>{"Only Show Overall Categories"}</div>
          </div>
        )}
        {(activeTab === "REPORT" || activeTab === "ONEX") && (
          <div
            className={styles.searchMainCheckboxWrapper}
            onClick={() => {
              if (!useOneXMetrics && groupBusinessSubgroups) {
                updateGroupBusinessSubgroups(false);
              }
              updateUseOneXMetrics(!useOneXMetrics);
            }}
            style={{ marginLeft: "20px" }}
          >
            <input
              className={styles.searchMainCheckbox}
              type="checkbox"
              checked={useOneXMetrics}
              onChange={() => {}}
              readOnly
            />
            <div className={styles.searchMainCheckboxText}>{"Use OneX Metrics in Charts"}</div>
          </div>
        )}
        {(activeTab === "REPORT" || activeTab === "ONEX") && (
          <div
            className={styles.searchMainCheckboxWrapper}
            onClick={() => {
              if (useOneXMetrics) {
                return;
              }
              updateGroupBusinessSubgroups(!groupBusinessSubgroups);
            }}
            style={{
              marginLeft: "20px"
            }}
          >
            <input
              className={styles.searchMainCheckbox}
              type="checkbox"
              checked={groupBusinessSubgroups}
              onChange={() => {}}
              readOnly
              disabled={useOneXMetrics}
            />
            <div
              className={styles.searchMainCheckboxText}
              style={{
                color: useOneXMetrics ? "#9E9DBC" : undefined,
                cursor: useOneXMetrics ? "not-allowed" : undefined
              }}
            >
              {"Group Subgroups by Type"}
            </div>
          </div>
        )}
      </div>
      <div className={styles.headerDashboardCharts}>
        {retCharts.map((chart, i) => {
          const chartTitle = chart?.title;
          const chartSummary = chartSummaries[chartTitle];

          const isActive = chartTitle === expandChart?.title;
          return (
            <div
              className={styles.headerChartWrapper + " " + (isActive ? styles.isActive : "")}
              key={"headerChart_" + i}
              onClick={() => {
                updateExpandChart(chart);
              }}
            >
              <KPIChart
                chart={chart}
                type={activeTab === "ONEX" ? "WATERFALL" : "GRAPH"}
                groupBusinessSubgroups={groupBusinessSubgroups}
                useOneXMetrics={useOneXMetrics}
                analysisResults={analysisResults}
                showRank
              />
              <KPISummary isHeader chart={chart} summary={chartSummary} isMainChart />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ChartRow = (props = {}) => {
  const {
    rank,
    chart,
    summary,
    type,
    sortKey,
    isActive,
    noHover,
    className,
    groupBusinessSubgroups,
    useOneXMetrics,
    analysisResults,
    ...retProps
  } = props;

  const chartCalcMode = useSelector((state) =>
    chatGPTReducer.getKPIAnalysisColumnCalcModeForKPI(state, chart.kpi_label)
  );

  const prevCount = getDisplayReferenceValueFromChart(chart, chartCalcMode);
  const newCount = getDisplayCurrentValueFromChart(chart, chartCalcMode);
  const displayCount = prevCount + " to " + newCount;

  let retTitle = chart.title;
  let forceNormalFont = false;
  let hasWaterfall = true;
  let hideChartValue = false;
  if (type === "REPORT") {
    if (chart.label) {
      retTitle = "";
      chart.superset?.forEach(() => {
        retTitle += "\t\t";
      });
      retTitle += chart.label;
      forceNormalFont = true;
      if (!chart.superset?.length) {
        retTitle = <strong>{retTitle}</strong>;
        forceNormalFont = false;
      }
    }
  } else if (type === "SUBGROUP") {
    if (chart.subset) {
      retTitle = chart.subset_label ?? chart.title;
    } else {
      retTitle = chart.label ?? chart.title;
    }

    hasWaterfall = false;
    hideChartValue = false;
  }

  return (
    <div
      className={
        styles.chartRowWrapper +
        " " +
        (isActive ? styles.isActive : "") +
        " " +
        (noHover ? styles.noHover : "") +
        " " +
        (className ?? "")
      }
      {...retProps}
    >
      <div className={styles.chartRowRank}>{rank}</div>
      <div className={styles.chartRowTitle}>{retTitle}</div>
      <div className={styles.chartRowChart}>
        <KPIChart
          chart={chart}
          numberStyle={
            forceNormalFont
              ? {
                  fontWeight: "normal"
                }
              : undefined
          }
          isSmall
          hideValue={hideChartValue}
          groupBusinessSubgroups={groupBusinessSubgroups}
          useOneXMetrics={useOneXMetrics}
        />
      </div>
      <div className={styles.chartRowCount}>{displayCount}</div>
      {hasWaterfall && (
        <div className={styles.chartRowChart}>
          <KPIWaterfallChart
            chart={chart}
            analysisResults={analysisResults}
            groupBusinessSubgroups={groupBusinessSubgroups}
            useOneXMetrics={useOneXMetrics}
            isSmall
          />
        </div>
      )}
      <div className={styles.chartRowSummary}>
        <KPISummary chart={chart} summary={summary} isSmall />
      </div>
    </div>
  );
};

const ChartRowHeader = (props = {}) => {
  const { className, title, type, ...retProps } = props;

  const hasWaterfall = type !== "SUBGROUP";

  return (
    <div
      className={styles.chartRowWrapper + " " + styles.noHover + " " + styles.chartRowHeader + " " + (className ?? "")}
      {...retProps}
    >
      <div className={styles.chartRowRank + " " + styles.chartRowHeader} />
      <div className={styles.chartRowTitle + " " + styles.chartRowHeader}>{title}</div>
      <div className={styles.chartRowChart + " " + styles.chartRowHeader}>{"% Change"}</div>
      <div className={styles.chartRowCount + " " + styles.chartRowHeader}>{"Actual Change"}</div>
      <div className={styles.chartRowSummary + " " + styles.chartRowHeader}>
        {hasWaterfall ? <strong>{"Why It Changed"}</strong> : null}
      </div>
    </div>
  );
};

const ChartCell = (props = {}) => {
  const {
    columnIndex,
    key,
    rowIndex,
    style,
    data,
    expandChart,
    chartSummaries,
    groupBusinessSubgroups,
    useOneXMetrics,
    columnCount,
    sortKey,
    onClick,
    onMouseEnter,
    onMouseLeave,
    type
  } = props;

  const retIndex = rowIndex * columnCount + columnIndex;
  const c = data[retIndex];

  if (!c) {
    return <div style={style} />;
  }

  const isActive = expandChart && c?.title === expandChart?.title;
  const retSummary = chartSummaries?.[c?.title];
  const retType = type ? type : undefined;

  let rowStyle;

  if (rowIndex % 2 === 0) {
    rowStyle = {
      background: "#F7F5FA"
    };
  }

  return (
    <div
      style={style}
      key={key ? "bodyChart_" + key : key}
      onClick={() => {
        if (onClick) {
          onClick(c);
        }
      }}
      onMouseEnter={() => {
        if (onMouseEnter) {
          onMouseEnter(c);
        }
      }}
      onMouseLeave={() => {
        if (onMouseLeave) {
          onMouseLeave(c);
        }
      }}
    >
      <ChartRow
        rank={rowIndex + 1}
        noHover={!onClick}
        chart={c}
        summary={retSummary}
        groupBusinessSubgroups={groupBusinessSubgroups}
        useOneXMetrics={useOneXMetrics}
        type={retType}
        sortKey={sortKey}
        style={rowStyle}
        isActive={isActive}
      />
    </div>
  );
};

const OneXBody = (props = {}) => {
  const { expandChart, updateExpandChart, searchVal, topLevelView, groupBusinessSubgroups, useOneXMetrics } = props;

  // We pretend we're on the "REPORT" tab for all values;
  const activeTab = "REPORT";

  const dispatch = useDispatch();

  const summarizeKPIChart = (chart) => dispatch(chatGPTReducer.summarizeKPIChart(chart?.kpi_label, chart));
  const chartSummaries = useSelector((state) => chatGPTReducer.getKPIChartSummaries(state));

  const analysisResults = useSelector((state) => chatGPTReducer.getKPIAnalysisResults(state));

  const KPISortKey = searchVal?.length ? "CHANGE" : "REPORT";

  const KPICharts = useSelector((state) => chatGPTReducer.getKPICharts(state));

  const retData = KPICharts.filter((d) => {
    return filterCharts(d, searchVal, KPISortKey);
  });

  // TODO: get latest date across all data in charts
  const rawLatestDate = retData[0]?.rawLatestDate;
  let latestEntry = rawLatestDate;
  retData.forEach((c) => {
    const latestData = c?.data?.[c?.data?.length - 1];
    const latestDate = latestData.date;
    if (!latestEntry || moment(latestEntry).isBefore(latestDate)) {
      latestEntry = latestDate;
    }
  });
  const latestMoment = moment(latestEntry);
  const weekStartMoment = moment(latestEntry).day(0);

  let sortKeyOverride = undefined;
  if (!topLevelView && activeTab === "REPORT") {
    sortKeyOverride = "CHANGE";
  }

  const KPITopCharts = useSelector((state) => chatGPTReducer.getKPITopCharts(state, sortKeyOverride)) ?? [];

  let retTopCharts = KPITopCharts.filter((d) => {
    // if (!KPISearchMain && KPISortKey === "REPORT" && searchVal?.length > 0) {
    //   return filterCharts(d, searchVal, "ACTUAL");
    // }
    return filterCharts(d, searchVal, sortKeyOverride ?? KPISortKey);
  }).slice(0, KPI_TOP_CHARTS_COUNT);

  return (
    <div className={styles.dashboardBody}>
      <KPIModal
        expandChart={expandChart}
        updateExpandChart={updateExpandChart}
        groupBusinessSubgroups={groupBusinessSubgroups}
        useOneXMetrics={useOneXMetrics}
      />
      <div className={styles.oneXTableWrapper}>
        <table>
          <tr>
            <th></th>
            <th colSpan={2}>{`WTD (${weekStartMoment.format("MM/DD")} - ${latestMoment.format("MM/DD")})`}</th>
          </tr>
          <tr>
            <th>Total - Areas</th>
            <th className={styles.redTableHeader} colSpan={2}>
              W/W - Like Days
            </th>
          </tr>
          <tr>
            <th>Total - Wireless</th>
            <th>Actual</th>
            <th>W/W</th>
          </tr>
          <tr>
            <td>{<strong>Postpaid Customer Growth</strong>}</td>
            <td></td>
            <td></td>
          </tr>
          {retData.map((c, i) => {
            const retSummary = chartSummaries?.[c?.title];

            let retTitle = c.fullLabel ?? c.title;

            let hideStats = false;

            let isHighlight = false;
            if (retTopCharts.find((topC) => topC.title === c.title)) {
              isHighlight = true;
            }

            if (c.label) {
              retTitle = "";
              if (c.superset?.length) {
                if (c.superset?.length === 1) {
                  retTitle += "\t\t";
                } else {
                  c.superset?.forEach(() => {
                    retTitle += "\t\t\t";
                  });
                }
              }
              retTitle += c.label;

              if (retTitle === "Upgrades") {
                hideStats = true;
              }
              if (!c.superset?.length) {
                const modifyTitles = [
                  "New Accounts",
                  "Gross Adds",
                  "Disconnects",
                  "Net Adds",
                  "Fixed Wireless Access",
                  "Upgrades"
                ];
                let modifier = "";
                if (modifyTitles.includes(retTitle)) {
                  modifier = "(K)";
                }
                retTitle = <strong>{retTitle + modifier}</strong>;
              }
            }

            let actualDiff = Math.round(c.newCount / 1000);
            let displayActual = actualDiff < 0 ? `(${formatNumber(Math.abs(actualDiff))})` : formatNumber(actualDiff);

            let valueDiff = Math.round(c.percentageChange);
            let displayValue =
              valueDiff < 0 ? `(${formatNumber(Math.abs(valueDiff), false)})%` : `${formatNumber(valueDiff, false)}%`;

            // Showing percentages for these special snowflakes
            if (c.title === "NEW_ACCOUNTS_SINGLE_PHONE" || c.title === "NEW_ACCOUNTS_MULTI_PHONE") {
              // Single Phone Mix = NEW_ACCOUNTS_SINGLE_PHONE / NEW_ACCOUNTS
              const newAccountsChart = analysisResults["NEW_ACCOUNTS"];

              const currTotalCount = newAccountsChart?.newCount || 0;
              const prevTotalCount = newAccountsChart?.previousCount || 0;

              const currCount = c.newCount;
              const prevCount = c.previousCount;

              const currDiff = (currCount / (currTotalCount || 1)) * 100;
              const prevDiff = (prevCount / (prevTotalCount || 1)) * 100;
              actualDiff = Math.round(currDiff);
              displayActual =
                actualDiff < 0
                  ? `(${formatNumber(Math.abs(actualDiff), false)})%`
                  : `${formatNumber(actualDiff, false)}%`;

              valueDiff = Math.round(currDiff - prevDiff);
              displayValue =
                valueDiff < 0 ? `(${formatNumber(Math.abs(valueDiff), false)})%` : `${formatNumber(valueDiff, false)}%`;
            } else if (c.title === "NEW_ACCOUNTS_PHONE_PREMIUM") {
              //Premium Mix% = NEW_ACCOUNTS_PHONE_PREMIUM/(NEW_ACCOUNTS_SINGLE_PHONE+NEW_ACCOUNTS_MULTI_PHONE)

              const singlePhoneChart = analysisResults["NEW_ACCOUNTS_SINGLE_PHONE"];
              const currSCount = singlePhoneChart?.newCount || 0;
              const prevSCount = singlePhoneChart?.previousCount || 0;
              const multiPhoneChart = analysisResults["NEW_ACCOUNTS_MULTI_PHONE"];
              const currMCount = multiPhoneChart?.newCount || 0;
              const prevMCount = multiPhoneChart?.previousCount || 0;

              const currCount = c.newCount;
              const prevCount = c.previousCount;

              const currDiff = (currCount / (currSCount + currMCount || 1)) * 100;
              actualDiff = Math.round(currDiff);
              displayActual =
                actualDiff < 0
                  ? `(${formatNumber(Math.abs(actualDiff), false)})%`
                  : `${formatNumber(actualDiff, false)}%`;

              const prevDiff = (prevCount / (prevSCount + prevMCount || 1)) * 100;
              valueDiff = Math.round(currDiff - prevDiff);
              displayValue =
                valueDiff < 0 ? `(${formatNumber(Math.abs(valueDiff), false)})%` : `${formatNumber(valueDiff, false)}%`;
            }

            return (
              <tr
                key={"onex_" + i}
                onClick={() => {
                  updateExpandChart(c);
                }}
                onMouseEnter={() => {
                  summarizeKPIChart(c);
                }}
                style={{ cursor: "pointer" }}
                className={
                  (!c.superset?.length ? styles.supersetRow : "") + " " + (isHighlight ? styles.highlightKPI : "")
                }
              >
                <td>
                  {retTitle}
                  <Tooltip
                    overlay={
                      <div style={{ width: 300, overflowY: "auto", padding: "4px" }}>
                        <KPISummary chart={c} summary={retSummary} />
                      </div>
                    }
                  >
                    <img className={styles.oneXTooltipIcon} src={MessageLineIcon} />
                  </Tooltip>
                </td>
                {hideStats ? (
                  <React.Fragment>
                    <td></td>
                    <td></td>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <td className={actualDiff < 0 ? styles.negativeValue : undefined}>
                      <Tooltip
                        overlay={
                          <div style={{ width: 600, overflowY: "auto" }}>
                            <div style={{ height: 150 }}>
                              <KPIWaterfallChart
                                chart={c}
                                analysisResults={analysisResults}
                                groupBusinessSubgroups={groupBusinessSubgroups}
                                useOneXMetrics={useOneXMetrics}
                              />
                            </div>
                            <div style={{}}>
                              <KPISummary chart={c} summary={retSummary} />
                            </div>
                          </div>
                        }
                      >
                        <img className={styles.oneXTooltipIconLeftSide} src={ChartWaterfallIcon} />
                      </Tooltip>
                      <span className={styles.oneXcellValue}>{displayActual}</span>
                    </td>
                    <td className={valueDiff < 0 ? styles.negativeValue : undefined}>
                      <Tooltip
                        overlay={
                          <div style={{ height: 200, width: 300 }}>
                            <KPIChart
                              chart={c}
                              numberStyle={{
                                fontWeight: "normal"
                              }}
                              groupBusinessSubgroups={groupBusinessSubgroups}
                              useOneXMetrics={useOneXMetrics}
                              analysisResults={analysisResults}
                            />
                          </div>
                        }
                      >
                        <img className={styles.oneXTooltipIconLeftSide} src={ChartLineIcon} />
                      </Tooltip>
                      <span className={styles.oneXcellValue}>{displayValue}</span>
                    </td>
                  </React.Fragment>
                )}
              </tr>
            );
          })}
        </table>
      </div>
      <div className={styles.oneXFooterWrapper}>
        <div className={styles.oneXFooterText} style={{ maxWidth: "698px" }}>
          {
            "Proprietary and Confidential. For internal Verizon use only. Use, disclosure or distribution outside Verizon Wireless is not permitted except by prior written approval."
          }
        </div>
        <div className={styles.oneXFooterText}>
          {
            "*Churn may be O/U stated for the first 5-to-10 days of the month, due to the limited days of actuals used in the run rate calculation."
          }
        </div>
        <div className={styles.oneXFooterText}>
          {
            "The use of individual|porting information for marketing and related activities is prohibited without prior approval by Legal."
          }
        </div>
      </div>
    </div>
  );
};

const DashboardBody = (props = {}) => {
  const { expandChart, updateExpandChart, searchVal, topLevelView, groupBusinessSubgroups, useOneXMetrics, activeTab } =
    props;

  const dispatch = useDispatch();
  const [summarizeWaiter, updateSummarizeWaiter] = useState();

  const chartSummaries = useSelector((state) => chatGPTReducer.getKPIChartSummaries(state));

  let KPISortKey = useSelector((state) => chatGPTReducer.getKPISortKey(state));

  const KPICharts = useSelector((state) => chatGPTReducer.getKPICharts(state));

  const handleRowHover = (chart) => {
    const chartKey = chart?.title ?? chart?.kpi_label;
    if (dispatch(chatGPTReducer.checkShouldAutoSummarize(chartKey))) {
      const timeoutId = setTimeout(() => {
        dispatch(chatGPTReducer.summarizeKPIChart(chartKey, chart));
        updateSummarizeWaiter();
      }, SUMMARIZE_HOVER_INTERVAL);
      updateSummarizeWaiter(timeoutId);
    }
  };

  const handleRowLeave = () => {
    if (summarizeWaiter) {
      clearTimeout(summarizeWaiter);
      updateSummarizeWaiter();
    }
  };

  const handleRowClick = (chart) => {
    const chartKey = chart?.title ?? chart?.kpi_label;
    dispatch(chatGPTReducer.summarizeKPIChart(chartKey, chart));
    updateExpandChart(chart);
  };

  const isReportView = topLevelView || !searchVal?.length;
  if (activeTab === "REPORT" && !isReportView) {
    KPISortKey = "CHANGE";
  }

  let retData = KPICharts.filter((d) => {
    return filterCharts(d, searchVal, KPISortKey);
  });

  if (retData.length === 0) {
    retData = KPICharts.filter((d) => {
      return filterCharts(d, "", KPISortKey);
    });
  }

  const chartRowType = activeTab === "REPORT" && isReportView ? "REPORT" : undefined;

  return (
    <div className={styles.dashboardBody}>
      <KPIModal
        expandChart={expandChart}
        updateExpandChart={updateExpandChart}
        groupBusinessSubgroups={groupBusinessSubgroups}
        useOneXMetrics={useOneXMetrics}
      />
      <div className={styles.dashboardBodyActions}>
        <div className={styles.dashboardActionTitle}>{"All Changes"}</div>
      </div>
      <div className={styles.dashboardCharts} style={{ height: "unset", minHeight: "unset", marginBottom: "4px" }}>
        <ChartRowHeader title={"What KPIs Changed"} type={chartRowType} />
      </div>
      <div className={styles.dashboardCharts}>
        <AutoSizer>
          {({ height, width }) => {
            const columnCount = 1;
            const columnWidth = Math.floor((width - 50) / columnCount);
            const rowCount = Math.ceil(retData.length / columnCount);

            return (
              <Grid
                cellRenderer={(props) => (
                  <ChartCell
                    {...props}
                    columnCount={columnCount}
                    data={retData}
                    expandChart={expandChart}
                    chartSummaries={chartSummaries}
                    groupBusinessSubgroups={groupBusinessSubgroups}
                    useOneXMetrics={useOneXMetrics}
                    type={chartRowType}
                    onClick={handleRowClick}
                    onMouseEnter={handleRowHover}
                    onMouseLeave={handleRowLeave}
                  />
                )}
                columnCount={columnCount}
                columnWidth={columnWidth}
                height={height}
                rowCount={rowCount}
                rowHeight={50}
                width={width}
              />
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

const ExecSummaryBody = () => {
  const summaryCharts = useSelector((state) => chatGPTReducer.getKPIExecutiveSummaryCharts(state));

  const chartSummaries = useSelector((state) => chatGPTReducer.getKPIChartSummaries(state));

  return (
    <div className={styles.execSummaryBody}>
      <div className={styles.execSummaryHeader}>{"Top Changes"}</div>
      {summaryCharts.map((chart, i) => {
        const chartTitle = chart?.title;
        const chartSummary = chartSummaries[chartTitle];
        return (
          <div className={styles.execSummaryItemWrapper} key={"summary_" + i}>
            <div className={styles.execSummaryItemContent}>
              <KPISummary chart={chart} summary={chartSummary} isExecSummary />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KPIChart = (props = {}) => {
  const {
    chart,
    analysisResults,
    groupBusinessSubgroups,
    useOneXMetrics,
    isSmall,
    numberStyle,
    hideValue,
    showRank,
    type
  } = props;

  if (!chart) {
    return <React.Fragment />;
  }

  const chartCalcMode = useSelector((state) =>
    chatGPTReducer.getKPIAnalysisColumnCalcModeForKPI(state, chart.kpi_label)
  );

  const displayValue = getDisplayValueFromChart(chart, undefined, chartCalcMode);
  const prevCount = getDisplayReferenceValueFromChart(chart, chartCalcMode);
  const newCount = getDisplayCurrentValueFromChart(chart, chartCalcMode);
  const displayCount = prevCount + " to " + newCount;

  if (isSmall) {
    return (
      <div className={styles.chartWrapper + " " + (isSmall ? styles.isSmall : "")}>
        <div className={styles.chartKPIChart + " " + (isSmall ? styles.isSmall : "")}>
          <KPILineChart chart={chart} isSmall={isSmall} />
        </div>
        {!hideValue && (
          <div className={styles.chartNumber + " " + (isSmall ? styles.isSmall : "")} style={numberStyle}>
            {displayValue}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartTitleWrapper}>
        {showRank && !isSmall && <KPIChartRank chart={chart} style={{ marginRight: "10px" }} />}
        <div className={styles.chartTitle + " " + (isSmall ? styles.isSmall : "")}>{chart.title}</div>
      </div>
      <div className={styles.chartContent + " " + (isSmall ? styles.isSmall : "")}>
        {!hideValue && (
          <div className={styles.chartValue}>
            <div className={styles.chartNumber + " " + (isSmall ? styles.isSmall : "")} style={numberStyle}>
              {displayValue}
            </div>
            <div className={styles.chartCount + " " + (isSmall ? styles.isSmall : "")}>{displayCount}</div>
          </div>
        )}
        <div className={styles.chartKPIChart + " " + (isSmall ? styles.isSmall : "")}>
          {type === "WATERFALL" ? (
            <KPIWaterfallChart
              chart={chart}
              isSmall={isSmall}
              analysisResults={analysisResults}
              groupBusinessSubgroups={groupBusinessSubgroups}
              useOneXMetrics={useOneXMetrics}
            />
          ) : (
            <KPILineChart chart={chart} isSmall={isSmall} />
          )}
        </div>
      </div>
    </div>
  );
};

const CustomToolTip = (props = {}) => {
  const { active, payload, isSmall, chart } = props;
  if (!active || !payload?.[0]?.payload) {
    return null;
  }

  const payloadData = payload[0].payload;

  let displayAverage;
  const isCurrentDate = useSelector((state) => chatGPTReducer.getIsCurrentDate(state, payloadData.date));
  const isReferenceDate = useSelector((state) => chatGPTReducer.getIsReferenceDate(state, payloadData.date));
  const chartCalcMode = useSelector((state) =>
    chatGPTReducer.getKPIAnalysisColumnCalcModeForKPI(state, chart.kpi_label)
  );
  const lookupTable = CHART_KEY_LOOKUPS[chartCalcMode] ?? CHART_KEY_LOOKUPS["SUM"];
  if (isCurrentDate) {
    displayAverage = chart?.[lookupTable?.CURRENT_VALUE];
  } else if (isReferenceDate) {
    displayAverage = chart?.[lookupTable?.REFERENCE_VALUE];
  }

  const dataKey = chartCalcMode === "AVERAGE" ? "average" : "value";
  const retValue = payloadData[dataKey];

  return (
    <div className={styles.cTooltip} style={{ opacity: isSmall ? 0.9 : undefined }}>
      <div>
        <span>{payloadData.date + ":"}</span>
        <strong style={{ marginLeft: "10px" }}>{formatNumber(retValue)}</strong>
      </div>
      <div></div>
      {displayAverage !== undefined && (
        <div>
          <span>{`Period Average: ${formatNumber(displayAverage)}`}</span>
        </div>
      )}
    </div>
  );
};

const KPILineChart = (props = {}) => {
  const { chart, isSmall, isLarge } = props;

  const rawData = chart?.data ?? [];

  const chartCalcMode = useSelector((state) =>
    chatGPTReducer.getKPIAnalysisColumnCalcModeForKPI(state, chart?.kpi_label)
  );
  const KPIDateRangeReferenceStart = useSelector((state) => chatGPTReducer.getKPIDateRangeReferenceStart(state));
  const KPIDateRangeReferenceEnd = useSelector((state) => chatGPTReducer.getKPIDateRangeReferenceEnd(state));
  const KPIDateRangeCurrentStart = useSelector((state) => chatGPTReducer.getKPIDateRangeCurrentStart(state));
  const KPIDateRangeCurrentEnd = useSelector((state) => chatGPTReducer.getKPIDateRangeCurrentEnd(state));

  const hasReferenceArea = !isSmall && chart?.hasNewData;

  const retData = useSelector((state) => chatGPTReducer.getKPIChartData(state, chart?.title, chart));

  const dataKey = chartCalcMode === "AVERAGE" ? "average" : "value";

  const referenceLineColor = "#4887F4";
  const referenceFillColor = "#7E69D733";
  let lineColor = referenceLineColor;
  let fillColor = referenceFillColor;

  const lookupTable = CHART_KEY_LOOKUPS[chartCalcMode] ?? CHART_KEY_LOOKUPS["SUM"];
  const chartPercentageChange = chart?.[lookupTable.PERCENTAGE_CHANGE];
  const referenceValue = chart[lookupTable.REFERENCE_VALUE];
  const currentValue = chart[lookupTable.CURRENT_VALUE];

  let yMin = rawData[0][dataKey];
  let yMax = rawData[0][dataKey];
  let xMin = rawData[0].date;
  let xMax = rawData[0].date;
  rawData.forEach((d) => {
    yMin = Math.min(d[dataKey], yMin);
    yMax = Math.max(d[dataKey], yMax);
    const currDate = moment(d.date);
    if (currDate.isBefore(xMin)) {
      xMin = d.date;
    } else if (currDate.isAfter(xMax)) {
      xMax = d.date;
    }
  });

  const yRange = getRange(yMin, yMax, true);

  if (chartPercentageChange > 0) {
    lineColor = "#008273";
    fillColor = "#8CEDB566";
  } else if (chartPercentageChange < 0) {
    lineColor = "#DC2626";
    fillColor = "#FFC5BE66";
  }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <LineChart width={width} height={height} data={retData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
          <ChartTooltip
            itemStyle={{ fontSize: "16px" }}
            contentStyle={{ fontSize: "16px" }}
            content={<CustomToolTip isSmall={isSmall} chart={chart} />}
          />
          <XAxis hide={isLarge ? false : true} dataKey={"date"} tick={isLarge ? true : false} />
          <Line
            dataKey={dataKey}
            stroke={lineColor}
            strokeWidth={isLarge ? 4 : 2}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
          {hasReferenceArea && (
            <React.Fragment>
              {KPIDateRangeReferenceStart === KPIDateRangeReferenceEnd ? (
                <ReferenceLine
                  stroke={referenceLineColor}
                  strokeWidth={isLarge ? 4 : 2}
                  strokeDasharray={"5 3"}
                  x={KPIDateRangeReferenceStart}
                />
              ) : (
                <ReferenceLine
                  stroke={referenceLineColor}
                  strokeWidth={isLarge ? 4 : 2}
                  strokeDasharray={"5 3"}
                  segment={[
                    {
                      x: KPIDateRangeReferenceStart,
                      y: referenceValue
                    },
                    {
                      x: KPIDateRangeReferenceEnd,
                      y: referenceValue
                    }
                  ]}
                />
              )}
              {KPIDateRangeCurrentStart === KPIDateRangeCurrentEnd ? (
                <ReferenceLine
                  stroke={referenceLineColor}
                  strokeWidth={isLarge ? 4 : 2}
                  strokeDasharray={"5 3"}
                  x={KPIDateRangeCurrentStart}
                />
              ) : (
                <ReferenceLine
                  stroke={referenceLineColor}
                  strokeWidth={isLarge ? 4 : 2}
                  strokeDasharray={"5 3"}
                  segment={[
                    {
                      x: KPIDateRangeCurrentStart,
                      y: currentValue
                    },
                    {
                      x: KPIDateRangeCurrentEnd,
                      y: currentValue
                    }
                  ]}
                />
              )}
              <ReferenceArea
                fill={fillColor}
                x1={KPIDateRangeCurrentStart}
                x2={KPIDateRangeCurrentEnd}
                y1={referenceValue}
                y2={currentValue}
              />
            </React.Fragment>
          )}
          <YAxis hide={!isLarge} tick={!!isLarge} domain={yRange} tickFormatter={(v) => formatNumber(v, false, true)} />
        </LineChart>
      )}
    </AutoSizer>
  );
};

const KPIWaterfallChart = (props = {}) => {
  const { chart, groupBusinessSubgroups, analysisResults, useOneXMetrics, isSmall, onHover, onMouseLeave, onClick } =
    props;

  const waterfallOptions = {
    groupBusinessSubgroups: groupBusinessSubgroups,
    useOneXMetrics: useOneXMetrics,
    analysisResultsOverride: analysisResults,
    chartOverride: chart
  };

  const retData = useSelector((state) =>
    chatGPTReducer.getKPIChartWaterfallData(state, chart?.title, waterfallOptions)
  );

  return (
    <WaterfallChart
      data={retData}
      isSmall={isSmall}
      hasAxis={!isSmall}
      onHover={onHover}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    />
  );
};

const KPISummary = (props = {}) => {
  const { isExecSummary, isMainChart, summary, isSmall, isHeader, className, chart, ...retProps } = props;

  const dispatch = useDispatch();

  const retSummary = summary;

  if (
    // No summary and not a main chart
    (!retSummary && !isMainChart) || // is an exec summary display and either has no summary or is errored
    (isExecSummary && (!retSummary || retSummary === "RETRY"))
  ) {
    return <div />;
  }

  const chartKey = chart?.title ?? chart?.kpi_label;

  if (!isExecSummary && (retSummary === "RETRY" || (isMainChart && !retSummary))) {
    return (
      <React.Fragment>
        <div
          className={
            styles.loadHolder + " " + (isHeader ? styles.isHeader : "") + " " + (isSmall ? styles.isSmall : "")
          }
        >
          {chart ? (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                console.log(chart);
                dispatch(chatGPTReducer.summarizeKPIChart(chartKey, chart));
              }}
              isSmall
            >
              {"Generate Summary"}
            </Button>
          ) : null}
        </div>
      </React.Fragment>
    );
  }

  if (retSummary === "LOADING") {
    return (
      <React.Fragment>
        <div
          className={
            styles.loadHolder + " " + (isHeader ? styles.isHeader : "") + " " + (isSmall ? styles.isSmall : "")
          }
        >
          <LoadingIndicator
            style={
              isSmall
                ? {
                    width: "20px",
                    height: "18px",
                    transform: "scale(0.40)",
                    margin: "-10px 10px 10px -5px"
                  }
                : {
                    margin: "0px"
                  }
            }
          />
          <div className={styles.loadLabel}>{"Summarizing..."}</div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <div
      className={
        styles.chartSummary +
        " " +
        (isHeader ? styles.isHeader : "") +
        " " +
        (isSmall ? styles.isSmall : "") +
        " " +
        (className ? className : "")
      }
      {...retProps}
    >
      <MarkdownPanel>{retSummary === "RETRY" ? "" : retSummary}</MarkdownPanel>
    </div>
  );
};

const KPIModal = (props = {}) => {
  const { expandChart, updateExpandChart, groupBusinessSubgroups, useOneXMetrics } = props;

  const dispatch = useDispatch();

  const nextId = useSelector((state) => nextReducer.getCurrentNextId(state));

  const savedChart = useSelector((state) =>
    chatGPTReducer.getKPIChart(state, expandChart?.title ?? expandChart?.kpi_label)
  );

  const retTitle = savedChart?.title ?? expandChart?.title;

  const analysisResults = useSelector((state) => chatGPTReducer.getKPIAnalysisResults(state));
  const chartSummaries = useSelector((state) => chatGPTReducer.getKPIChartSummaries(state));

  const KPISortKey = useSelector((state) => chatGPTReducer.getKPISortKey(state));

  const [selectedChart, updateSelectedChart] = useState();
  const [viewMode, setViewMode] = useState("ANALYSIS");
  const [summarizeWaiter, updateSummarizeWaiter] = useState();

  const handleClose = () => {
    updateExpandChart(undefined);
    setViewMode("ANALYSIS");
  };

  const getChartCalcMode = (kpiLabel) =>
    dispatch((dispatch, getState) => {
      return chatGPTReducer.getKPIAnalysisColumnCalcModeForKPI(getState(), kpiLabel);
    });

  useEffect(() => {
    updateSelectedChart(undefined);
  }, [expandChart]);

  const handleRowHover = (chart) => {
    const chartKey = chart?.title ?? chart?.kpi_label;
    if (dispatch(chatGPTReducer.checkShouldAutoSummarize(chartKey))) {
      const timeoutId = setTimeout(() => {
        dispatch(chatGPTReducer.summarizeKPIChart(chartKey, chart));
        updateSummarizeWaiter();
      }, SUMMARIZE_HOVER_INTERVAL);
      updateSummarizeWaiter(timeoutId);
    }
  };
  const handleRowLeave = () => {
    if (summarizeWaiter) {
      clearTimeout(summarizeWaiter);
      updateSummarizeWaiter();
    }
  };

  const handleRowClick = (chart) => {
    const chartKey = chart?.title ?? chart?.kpi_label;
    dispatch(chatGPTReducer.summarizeKPIChart(chartKey, chart));
    updateSelectedChart(selectedChart?.title === chart?.title ? undefined : chart);
  };

  const handleWaterfallClick = (chartTitle) => {
    if (!chartTitle) {
      return;
    }
    const chart = dispatch(chatGPTReducer.fetchKPIChart(chartTitle));

    return handleRowClick(chart);
  };

  let displayValue;
  let displayCount;

  let retChart;
  let retSummary;

  let retData;
  let showWaterfall = false;

  let displayChart;

  if (expandChart) {
    retChart = savedChart ?? expandChart;
    displayChart = retChart;
    let summaryTitle = retTitle;
    if (selectedChart) {
      displayChart = selectedChart;
      summaryTitle = selectedChart.title;
    }
    const chartCalcMode = getChartCalcMode(displayChart?.kpi_label);
    displayValue = getDisplayValueFromChart(displayChart, undefined, chartCalcMode);
    const prevCount = getDisplayReferenceValueFromChart(displayChart, chartCalcMode);
    const newCount = getDisplayCurrentValueFromChart(displayChart, chartCalcMode);

    displayCount = prevCount + " to " + newCount;
    retSummary = chartSummaries[summaryTitle];
    retData = retChart?.subgroups;
    if (retData?.length && !selectedChart) {
      showWaterfall = true;
    }
  }

  const targetKPI = retChart?.kpi_label || expandChart?.kpi_label;

  // Used for opening the dashboard in a new tab
  const hasKPIDeepDive = useSelector((state) => chatGPTReducer.getHasKPIDeepDive(state, targetKPI));
  const KPIDeepDiveNextId = useSelector((state) => chatGPTReducer.getKPIDeepDiveNextId(state, targetKPI));
  const KPIDeepDiveDatasetId = useSelector((state) => chatGPTReducer.getKPIDeepDiveDatasetId(state, targetKPI));

  const chartRowType = "SUBGROUP";

  return (
    <Dialog open={expandChart !== undefined} onOpenChange={handleClose}>
      <DialogContent
        size="full"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className={styles.KPIModalHeader}>
            <KPIChartRank chart={retChart} style={{ marginRight: "10px" }} />
            {retChart?.title}

            {String(nextId) !== "new" && (
              <ButtonGroup style={{ marginLeft: "20px" }}>
                <Button onClick={() => setViewMode("ANALYSIS")} active={viewMode === "ANALYSIS"}>
                  {"KPI Details"}
                </Button>
                {selectedChart === undefined && (
                  <Button onClick={() => setViewMode("WHATS_CHANGED")} active={viewMode === "WHATS_CHANGED"}>
                    {`What's Changed Deep Dive for ${retChart?.fullLabel ?? "this KPI"}`}
                  </Button>
                )}
              </ButtonGroup>
            )}
            {/* Temporarily Removed due to time limit. We want this to open the link in a new tab */}
            {viewMode === "WHATS_CHANGED" && hasKPIDeepDive && (
              <div style={{ marginLeft: "auto", marginRight: "20px" }}>
                {Boolean(KPIDeepDiveNextId && KPIDeepDiveDatasetId) && (
                  <Button
                    style={{ marginRight: "10px" }}
                    onClick={() => {
                      const shouldReset = confirm(
                        `This will remove the current Deep Dive for ${targetKPI} from this project. The deep dive will still be available in your saved sessions, but will no longer be accessible through this project. Do you want to proceed?`
                      );
                      if (shouldReset) {
                        dispatch(chatGPTReducer.resetKPIDeepDive(targetKPI));
                      }
                    }}
                  >
                    {"Reset Deep Dive"}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    const origin = window.location?.origin;
                    const url = origin + "/next/" + KPIDeepDiveDatasetId + "/" + KPIDeepDiveNextId;
                    window.open(url, "_blank");
                  }}
                >
                  {"Open in New Tab"}
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        {expandChart && (
          <div className={styles.KPIModalBody}>
            {selectedChart && (
              <div className={styles.KPIModalHeader} style={{ paddingLeft: 0, width: "100%" }}>
                <KPIChartRank chart={displayChart} style={{ marginRight: "10px" }} />
                {selectedChart?.subset_label ?? selectedChart?.label}
                <Button
                  style={{ marginLeft: "auto", fontWeight: "normal" }}
                  onClick={() => updateSelectedChart(undefined)}
                >
                  {"Back"}
                </Button>
              </div>
            )}
            {viewMode === "WHATS_CHANGED" && String(nextId) !== "new" ? (
              <div className={styles.KPIDeepDiveHolder}>
                <KPIDeepDive targetKPI={targetKPI} />
              </div>
            ) : (
              <React.Fragment>
                <div className={styles.modalCharts}>
                  <div className={styles.modalChartItem} style={{ flexBasis: 300 }}>
                    <div className={styles.modalKPILabel}>{`What's Changed for ${retChart?.fullLabel}`}</div>
                    <div className={styles.modalKPIChartWrapper}>
                      <div className={styles.chartValue}>
                        <div className={styles.chartNumber}>{displayValue}</div>
                        <div className={styles.chartCount}>{displayCount}</div>
                      </div>
                      <div className={styles.KPIModalChart}>
                        <KPILineChart chart={displayChart} isLarge />
                      </div>
                    </div>
                  </div>
                  {showWaterfall && (
                    <div className={styles.modalChartItem} style={{ flexBasis: 500 }}>
                      <div className={styles.modalKPILabel}>{"Why It Changed"}</div>
                      <div className={styles.KPIModalWaterfall}>
                        <KPIWaterfallChart
                          chart={retChart}
                          onClick={handleWaterfallClick}
                          isLarge
                          analysisResults={analysisResults}
                          groupBusinessSubgroups={groupBusinessSubgroups}
                          useOneXMetrics={useOneXMetrics}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.modalSummaries}>
                  {/* <div className={styles.modalKPILabel}>
                  {"Summary of the Changes"}
                </div> */}
                  <KPISummary chart={retChart} summary={retSummary} isMainChart />
                </div>
                {retData && (
                  <React.Fragment>
                    <div
                      className={styles.modalChartTable}
                      style={{ height: "unset", minHeight: "unset", marginBottom: "4px" }}
                    >
                      <ChartRowHeader title={"Changes For Each Factor"} type={chartRowType} />
                    </div>
                    <div className={styles.modalChartTable}>
                      <AutoSizer>
                        {({ height, width }) => {
                          const minColumnWidth = 280;
                          // const columnCount = Math.floor((width - 50) / minColumnWidth);
                          const columnCount = 1;
                          const columnWidth = Math.floor((width - 50) / columnCount);
                          const rowCount = Math.ceil(retData.length / columnCount);

                          return (
                            <Grid
                              cellRenderer={(props) => (
                                <ChartCell
                                  {...props}
                                  columnCount={columnCount}
                                  data={retData}
                                  expandChart={selectedChart}
                                  chartSummaries={chartSummaries}
                                  sortKey={KPISortKey}
                                  onMouseEnter={handleRowHover}
                                  onMouseLeave={handleRowLeave}
                                  onClick={handleRowClick}
                                  type={chartRowType}
                                />
                              )}
                              columnCount={columnCount}
                              columnWidth={columnWidth}
                              height={height}
                              rowCount={rowCount}
                              rowHeight={50}
                              width={width}
                            />
                          );
                        }}
                      </AutoSizer>
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const KPIChartRank = (props = {}) => {
  const { chart, ...retProps } = props;

  const chartRank = useSelector((state) => chatGPTReducer.getKPIChartRank(state, chart?.title, chart));

  if (!chart) {
    return <React.Fragment />;
  }

  return (
    <div className={styles.KPIChartRankBadge} {...retProps}>
      <div className={styles.KPIChartRankBadgeText}>{chartRank}</div>
    </div>
  );
};

export default KPIDashboard;
